import { StylesResultProps, useEditor } from "@grapesjs/react";
import { Box, Grid } from "@mui/material";
import { AccordianChartBuilderSidebarBlock } from "features/chartLibrary";
import LightningLandersStyleFields from "features/lightningLanders/components/LightningLandersStyleFields";
import { useStylesUpdate } from "features/lightningLanders/providers/StylesUpdateProvider";
import debounce from "lodash/debounce";
import { useEffect, useMemo, useState } from "react";

const FLEX_ITEM_SECTOR_ID = "flex-item";
const FLEX_DISPLAY_VALUES = ["flex", "inline-flex"];

function isParentFlexContainer(editor: NonNullable<ReturnType<typeof useEditor>>): boolean {
  try {
    const selected = editor.getSelected();
    if (!selected) return false;
    const parent = selected.parent();
    if (!parent) return false;
    const el = parent.getEl();
    if (!el || typeof window === "undefined") return false;
    const display = window.getComputedStyle(el).display;
    return FLEX_DISPLAY_VALUES.includes(display);
  } catch {
    return false;
  }
}

function LightningLandersStyles({
  sectors,
}: Omit<StylesResultProps, "Container">) {
  const editor = useEditor();
  const { updateTrigger, triggerUpdate } = useStylesUpdate();
  const [openSector, setOpenSector] = useState<string | null>(null);

  useEffect(() => {
    if (!editor) return;
    const selected = editor.getSelected();
    if (!selected) {
      setTimeout(() => {
        setOpenSector(null);
      }, 200);
    }
  }, [editor, updateTrigger]);

  // Debounce so rapid style/selection changes (e.g. after block drop) trigger one panel update
  const STYLES_DEBOUNCE_MS = 120;
  useEffect(() => {
    if (!editor) return;
    const onStyleUpdate = debounce(() => triggerUpdate(), STYLES_DEBOUNCE_MS);
    editor.on("style:property:update", onStyleUpdate);
    editor.on("component:select", onStyleUpdate);
    return () => {
      onStyleUpdate.cancel();
      editor.off("style:property:update", onStyleUpdate);
      editor.off("component:select", onStyleUpdate);
    };
  }, [editor, triggerUpdate]);

  const selectedId = editor?.getSelected()?.getId();
  const visibleSectors = useMemo(() => {
    if (!editor) return sectors;
    const showFlexItem = isParentFlexContainer(editor);
    return sectors.filter((sector) => {
      if (sector.getId() === FLEX_ITEM_SECTOR_ID) return showFlexItem;
      return true;
    });
    // selectedId + updateTrigger: re-run when selection or parent styles change (Flex Item visibility)
  }, [sectors, editor, updateTrigger, selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStyleChange = (propertyId: string, value: string) => {
    if (!editor) return;
    const target = editor.StyleManager.getSelected();
    if (target) {
      const selectorName = target.get("name");
      const rule =
        editor.CssComposer.getRule(`.${selectorName}`) ||
        editor.CssComposer.addRules(`.${selectorName} {}`)[0];
      rule.setStyle({ [propertyId]: value });
    }
  };

  if (!editor) return null;

  return (
    <Box sx={{ flexGrow: 1 }}>
      {visibleSectors.map((sector, i) => (
        <AccordianChartBuilderSidebarBlock
          key={sector.getId()}
          title={sector.getName()}
          sx={{ padding: 2 }}
          defaultClosed={openSector !== sector.getId()}
          forcetopborder={i === 0}
          forcebottomborder
          onOpenClose={(open) => {
            setOpenSector(open ? sector.getId() : null);
          }}
        >
          <Grid spacing={2} container>
            {sector.getProperties().map((prop, i) => (
              <LightningLandersStyleFields
                key={i}
                prop={prop}
                onChange={(event: any) =>
                  handleStyleChange(prop.getId(), event.target.value)
                }
                sector={sector}
              />
            ))}
          </Grid>
        </AccordianChartBuilderSidebarBlock>
      ))}
    </Box>
  );
}

export default LightningLandersStyles;
